/**
 * Email Verification Page
 * Handles email verification when user clicks the link in their email
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase/config';
import { ROUTES } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/common/Layout';
import { Card } from '../components/common/Card';

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [redirectTo, setRedirectTo] = useState<string>('');
  const { refreshUser, user } = useAuth();

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email and try again.');
        return;
      }

      try {
        const verifyEmail = httpsCallable(functions, 'verifyEmail');
        const result = await verifyEmail({ token });
        const data = result.data as { success: boolean; message: string; alreadyVerified?: boolean; redirectTo?: string };

        if (data.success) {
          if (data.redirectTo && data.redirectTo.startsWith('/')) {
            setRedirectTo(data.redirectTo);
          }

          try {
            await refreshUser();
          } catch (refreshError) {
            console.error('Error refreshing user after verification:', refreshError);
          }

          setStatus('success');
          setMessage(data.alreadyVerified
            ? 'Your email is already verified!'
            : 'Email verified successfully!');
          
          // Start countdown for redirect
          const interval = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(interval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return () => clearInterval(interval);
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed. Please try again.');
        }
      } catch (error: any) {
        console.error('Error verifying email:', error);
        
        let errorMessage = 'An error occurred during verification.';
        
        if (error.code === 'functions/not-found') {
          errorMessage = 'Invalid verification link. The link may have expired or been used already.';
        } else if (error.code === 'functions/deadline-exceeded') {
          errorMessage = 'Verification link has expired. Please request a new one.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setStatus('error');
        setMessage(errorMessage);
      }
    };

    verifyToken();
  }, [searchParams, refreshUser]);

  /**
   * Decide where to send the user after successful email verification.
   * Priority order:
   *  1. Backend-provided redirectTo (rare, e.g. admin-triggered verification)
   *  2. intendedDestination on user doc
   *  3. Pending purchase in sessionStorage (12-month package → /billing)
   *  4. Twelve-month package users → dashboard
   *  5. Completed hero assessment (same tab) → learning plan
   *  6. Assessment already completed (cross-tab) → learning plan
   *  7. Cold signup (no assessment yet) → skills assessment
   *  8. Fallback → login
   */
  const getPostVerificationRoute = (): string => {
    if (redirectTo) return redirectTo;

    if (!user) return ROUTES.login;

    // Product-specific redirects
    if (user.intendedDestination) return user.intendedDestination;

    // Pending purchase flow
    if (sessionStorage.getItem('intendedPurchase')) return '/billing';

    // Twelve-month package users already have full access
    if (user.subscriptionTier === 'twelve_month_package') return ROUTES.dashboard;

    // User completed the hero assessment before registering
    const heroAnswers = sessionStorage.getItem('heroAssessmentAnswers');
    if (heroAnswers || user.skillsAssessmentCompleted) return ROUTES.learningPlan;

    // Cold signup — no assessment done yet
    return ROUTES.skillsAssessment;
  };

  // Effect for navigation when countdown reaches 0
  useEffect(() => {
    if (status === 'success' && countdown === 0) {
      navigate(getPostVerificationRoute());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, countdown, redirectTo, user, navigate]);

  const handleContinue = () => {
    navigate(getPostVerificationRoute());
  };

  return (
    <Layout showFooter={false}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <Card>
            {status === 'verifying' && (
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying your email...</h2>
                <p className="text-gray-600">Please wait while we verify your email address.</p>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Email Verified!</h2>
                <p className="text-gray-600 mb-4">{message}</p>
                <p className="text-sm text-gray-500 mb-6">
                  Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
                </p>
                <button
                  onClick={handleContinue}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
                >
                  Continue
                </button>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Verification Failed</h2>
                <p className="text-gray-600 mb-6">{message}</p>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate(ROUTES.dashboard)}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={() => navigate(ROUTES.login)}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-all"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};
